import { SelectFieldOption } from "controls/global/select-field/SelectInput";
import {
  Jacket,
  JacketFormDetail,
} from "entities/UIModel";
import { TransCode } from "utils/context/StewartAccessContext";
import { Translation } from "utils/context/TranslationContext";
import {
  JacketFormDetailName,
  JacketFormType,
  OrderStatusType,
  OrderStatusTypeName,
} from "utils/data/enum";
import { isCollateralMortgageTransCode } from "./collateralMortgage";
import {
  formatDate,
  formatDateTime,
} from "utils/shared";
import { buildTransCodeKey, getFormDetailTranslatedName } from "../helper";

const getJacketTabTitle = (index: number) => `Jacket ${index + 1}`;

// The text shown next to the title on a collapsed jacket. Kept here so the
// "Attach to Primary Loan" options read exactly like the collapsed jacket header.
const getJacketTabDescription = (jacket: Jacket) => {
  if (!jacket?.formType || !jacket?.form) return "";

  const issuedDate = jacket.issueDateTimeStr
    ? formatDateTime(new Date(jacket.issueDateTimeStr), false)
    : "";

  const statusDate =
    jacket.orderStatusTypeCode === OrderStatusType.Voided
      ? formatDate(new Date(String(jacket.void?.dateTime)))
      : formatDate(new Date(issuedDate));

  const jacketStatus = jacket.orderStatusTypeName
    ? jacket.orderStatusTypeName
    : OrderStatusTypeName.Pending;

  const policyNumber = jacket.productReferenceID;

  if (jacket.orderStatusTypeCode === OrderStatusType.Pending) {
    return ` ${jacket.form} - ${jacketStatus}`;
  }

  return ` ${jacket.form} - ${jacketStatus} ${statusDate} [${policyNumber}]`;
};

// Superseded by the API's isPrimaryLoan flag, which now decides eligibility. Kept for
// reference in case the client-side inference is needed again.
// An issued Loan jacket a Collateral Mortgage can attach to. A Collateral
// Mortgage is itself a Loan jacket, so one can never be the primary loan for
// another — those are excluded by trans code. When the state has no configured
// collateral trans code there is nothing to exclude.
// const isPrimaryLoanCandidate = (
//   jacket: Jacket,
//   collateralMortgageTransCode: string
// ) =>
//   jacket?.formType === JacketFormType.Loan &&
//   jacket?.orderStatusTypeCode === OrderStatusType.Issued &&
//   !isCollateralMortgageTransCode(
//     jacket?.pricingDetail?.transCode,
//     collateralMortgageTransCode
//   );

// Eligibility comes from the API's isPrimaryLoan flag.
const isPrimaryLoanJacket = (jacket: Jacket) => jacket?.isPrimaryLoan === 1;

// The jacket being edited is excluded so it can never attach to itself.
const getIssuedLoanJacketOptions = (
  jackets: Jacket[] | undefined,
  excludeIndex: number
): SelectFieldOption[] =>
  (jackets || [])
    .map((jacket, index) => ({ jacket, index }))
    .filter(
      ({ jacket, index }) => index !== excludeIndex && isPrimaryLoanJacket(jacket)
    )
    .map(({ jacket, index }) => ({
      value: String(jacket.orderID),
      text: `${getJacketTabTitle(index)} - ${getJacketTabDescription(jacket)}`,
    }));

const hasIssuedLoanJacket = (
  jackets: Jacket[] | undefined,
  excludeIndex: number
) => getIssuedLoanJacketOptions(jackets, excludeIndex).length > 0;

// The Collateral Mortgages still hanging off a given primary Loan jacket. Voided ones no
// longer hold the loan open, and a jacket with no OrderID has never been issued, so
// nothing can be attached to it — guarding that also stops null matching null below.
// The array index rides along so callers can label each one "Jacket N" like the
// "Attach to Primary Loan" options do.
const getAttachedCollateralMortgages = (
  jackets: Jacket[] | undefined,
  primaryLoanOrderID: number | undefined,
  collateralMortgageTransCode: string
): Array<{ jacket: Jacket; index: number; }> => {
  if (!primaryLoanOrderID || !collateralMortgageTransCode) return [];

  return (jackets || [])
    .map((jacket, index) => ({ jacket, index }))
    .filter(
      ({ jacket }) =>
        isCollateralMortgageTransCode(
          jacket?.pricingDetail?.transCode,
          collateralMortgageTransCode
        ) &&
        jacket?.orderStatusTypeCode !== OrderStatusType.Voided &&
        String(jacket?.primaryLoanOrderID) === String(primaryLoanOrderID)
    );
};

const isValidPairForSimultaneous = (jackets: Jacket[]) => {
  let isValidPair = false;

  if (
    jackets.length >= 2 &&
    ((jackets[0].formType === JacketFormType.Loan &&
      jackets[1].formType === JacketFormType.Owners) ||
      (jackets[0].formType === JacketFormType.Owners &&
        jackets[1].formType === JacketFormType.Loan))
  ) {
    isValidPair = true;
  }

  return isValidPair;
};

const isFormTypeValidForSimultaneous = (type: string) =>
  type === JacketFormType.Loan.toString() ||
  type === JacketFormType.Owners.toString();

const isAllowedJacketType = (currentJacket: Jacket, nextJacket: Jacket) =>
  currentJacket && nextJacket && currentJacket.formType !== nextJacket.formType;

const isJacketValidForSimultaneous = (jacket: Jacket) =>
  jacket &&
  isFormTypeValidForSimultaneous(jacket.formType) &&
  (!jacket.orderStatusTypeCode ||
    jacket.orderStatusTypeCode === OrderStatusType.Pending) &&
  !jacket.pricingDetail?.isSimultaneous;

const shouldUncheckSimultaneousJacket = (jacket: Jacket) =>
  jacket &&
  (!jacket.orderStatusTypeCode ||
    jacket.orderStatusTypeCode === OrderStatusType.Pending) &&
  jacket.pricingDetail?.isSimultaneous;

const combineFileAndLookupFormDetails = (
  currentFormID: string,
  orderStatusTypeCode: string,
  validBorrowerName: string,
  isRevising: boolean,
  existingFormDetails?: Array<JacketFormDetail>,
  apiFormDetails?: Array<JacketFormDetail>,
  translations?: Array<Translation>
) => {
  let fileAndApiFormDetails: JacketFormDetail[] = [];
  let missingFormDetails: JacketFormDetail[] = [];

  if (apiFormDetails && apiFormDetails?.length > 0) {
    fileAndApiFormDetails = apiFormDetails?.map((afd) => {
      if (existingFormDetails && existingFormDetails.length > 0) {
        let existingDetail = existingFormDetails.find(
          (efd) => efd.formDetailName === afd.formDetailName
        );

        if (!existingDetail && translations && translations.length > 0) {
          const translationMatch = checkForFormDetails(
            afd.formDetailName || "",
            existingFormDetails,
            translations
          );
          if (translationMatch) existingDetail = translationMatch;
        }

        if (existingDetail) {
          //Translate formDetailNames
          existingDetail.formDetailDisplayName = getFormDetailTranslatedName(
            existingDetail.formDetailName,
            translations,
            true
          );
          if (
            isRevising ||
            (orderStatusTypeCode !== OrderStatusType.Issued &&
              orderStatusTypeCode !== OrderStatusType.Voided)
          ) {
            existingDetail.formID = currentFormID;
            existingDetail.formDetailID = afd.formDetailID;
          }
          return existingDetail; // return the combined form detail
        }
      }

      // No existingDetail found
      afd.formDetailValue = getInitialValue(
        afd.formDetailName || "",
        validBorrowerName
      );
      missingFormDetails.push(afd);
      return afd;
    });
  }

  return { fileAndApiFormDetails, missingFormDetails };
};

const checkForFormDetails = (
  searchItem: string,
  existingFormDetails?: Array<JacketFormDetail>,
  translations?: Array<any>
) => {
  const matchingTranslation = translations?.find(
    (t) => t.source1 === searchItem || t.target1 === searchItem
  );
  if (matchingTranslation) {
    const matchFound = existingFormDetails?.find(
      (fd) =>
        matchingTranslation.source1 === fd.formDetailName ||
        matchingTranslation.target1 === fd.formDetailName
    );
    return matchFound;
  }
};

const getInitialValue = (name: string, validBorrowerName?: string) => {
  if (name === JacketFormDetailName.BorrowerNamesAndVesting)
    return validBorrowerName;
  if (name === JacketFormDetailName.AddendumAttached) return "N";
};

const buildTransCodeOption = (
  transCode: TransCode,
  currentTransCode: string
): SelectFieldOption => {
  const value = transCode["@Value"];
  const description = transCode["@Description"];
  const key = buildTransCodeKey(value, description);

  return {
    text: key,
    value: key,
    desc: description,
    selected: Boolean(currentTransCode === value),
    originalValue: value,
  };
};

// const buildTransCodeKey = (code?: string, desc?: string): string => {
//   if (!code || !desc) return "";
//   return `${code} - ${desc}`;
// };

// PricingConfig-Value template: "XXX - Description"
// ex: 123 - Default TransCode (Jacket)
const getTransCodeKey = (
  text: string
): { code: string; description: string; } => {
  const idx = text.indexOf("-");
  const [code, desc] = [text.slice(0, idx), text.slice(idx + 1)];

  return {
    code: code ? code.trim() : "",
    description: desc ? desc.trim() : "",
  };
};

const setDirtyInQnA = (apiSCFile: any, jacketInQnA: Jacket) => {
  const { integrationKey } = jacketInQnA;
  const { JacketProducts } = apiSCFile;
  const dirtyJacketProducts = JacketProducts.map((product: any) => {
    if (product.IntegrationKey === integrationKey) {
      const dirtyEndorsements = product.Endorsements.map((endorsement: any) => {
        return {
          ...endorsement,
          IsDirty: true,
        };
      });
      return {
        ...product,
        Endorsements: dirtyEndorsements,
        IsDirty: true,
      };
    } else return product;
  });

  return {
    ...apiSCFile,
    JacketProducts: dirtyJacketProducts,
  };
};

export {
  // buildTransCodeKey,
  buildTransCodeOption, combineFileAndLookupFormDetails, getAttachedCollateralMortgages, getIssuedLoanJacketOptions, getJacketTabDescription, getJacketTabTitle, getTransCodeKey, hasIssuedLoanJacket, isAllowedJacketType, isFormTypeValidForSimultaneous, isJacketValidForSimultaneous, isPrimaryLoanJacket,
  isValidPairForSimultaneous, setDirtyInQnA, shouldUncheckSimultaneousJacket
};

